import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, forkJoin, interval, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { global_user } from '../global/global';

export type UnreadChannel = 'whatsapp' | 'telegram' | 'email' | 'phone';

/** The menu link each channel's unread count belongs to, and the bot API prefix that serves it. */
export const UNREAD_CHANNELS: ReadonlyArray<{ source: UnreadChannel; url: string; api: string }> = [
  { source: 'whatsapp', url: '/pages/whatsapp_bot', api: 'whatsapp_bot' },
  { source: 'telegram', url: '/pages/telegram_bot', api: 'telegram_bot' },
  { source: 'email', url: '/pages/email_bot', api: 'email_bot' },
  { source: 'phone', url: '/pages/telephone', api: 'telephone' },
];

const POLL_MS = 30_000;

/**
 * Unread conversation counts per channel, keyed by the channel's menu URL, so the menu can show
 * "there is something new" without the user opening each channel page.
 */
@Injectable({ providedIn: 'root' })
export class ChannelUnreadService implements OnDestroy {
  private readonly countsSubject = new BehaviorSubject<Record<string, number>>({});
  readonly counts$: Observable<Record<string, number>> = this.countsSubject.asObservable();

  private watched: UnreadChannel[] = [];
  private poll?: Subscription;
  private fetch?: Subscription;
  private readonly onVisible = () => {
    if (document.visibilityState === 'visible') this.refresh();
  };

  constructor(private http: HttpClient, private zone: NgZone) {}

  /** Polls only the channels whose menu links are shown, so hidden channels cost no requests. */
  watch(menuUrls: string[]): void {
    const next = UNREAD_CHANNELS.filter((c) => menuUrls.includes(c.url)).map((c) => c.source);
    if (next.join() === this.watched.join() && this.poll) return;
    this.watched = next;
    this.stop();
    if (!next.length) {
      this.countsSubject.next({});
      return;
    }
    this.refresh();
    this.poll = interval(POLL_MS).subscribe(() => {
      if (document.visibilityState !== 'hidden') this.refresh();
    });
    document.addEventListener('visibilitychange', this.onVisible);
  }

  refresh(): void {
    const userId = global_user?.user?.id;
    if (!userId || !this.watched.length) return;
    const channels = UNREAD_CHANNELS.filter((c) => this.watched.includes(c.source));
    this.fetch?.unsubscribe();
    this.fetch = forkJoin(
      channels.map((c) =>
        this.http
          .get<unknown>(`${environment.baseUrl}/${c.api}/get/unread/threads`, {
            params: { user_id: userId, source: c.source },
          })
          // A failed channel keeps its last count instead of flashing to zero.
          .pipe(
            map((ids) => (Array.isArray(ids) ? new Set(ids.map(String)).size : 0)),
            catchError(() => of(this.countsSubject.value[c.url] ?? 0))
          )
      )
    ).subscribe((sizes) => {
      const counts = { ...this.countsSubject.value };
      channels.forEach((c, i) => (counts[c.url] = sizes[i]));
      this.publish(counts);
    });
  }

  /** A channel page that already holds the unread list hands its size over, so the menu updates at once. */
  report(source: UnreadChannel, count: number): void {
    const channel = UNREAD_CHANNELS.find((c) => c.source === source);
    if (!channel) return;
    this.publish({ ...this.countsSubject.value, [channel.url]: count });
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private publish(counts: Record<string, number>): void {
    const prev = this.countsSubject.value;
    const changed =
      Object.keys(counts).length !== Object.keys(prev).length ||
      Object.keys(counts).some((url) => counts[url] !== prev[url]);
    if (changed) this.zone.run(() => this.countsSubject.next(counts));
  }

  private stop(): void {
    this.poll?.unsubscribe();
    this.fetch?.unsubscribe();
    this.poll = undefined;
    document.removeEventListener('visibilitychange', this.onVisible);
  }
}

/** Copies the menu with an unread badge on each channel link that has one. */
export function withUnreadBadges<T extends { url?: unknown; badge?: unknown }>(
  items: T[],
  counts: Record<string, number>
): T[] {
  return items.map((item) => {
    const count = typeof item.url === 'string' ? counts[item.url] ?? 0 : 0;
    if (!count) return item;
    return {
      ...item,
      badge: { color: 'danger', text: count > 99 ? '99+' : String(count), class: 'nav-unread-badge' },
    };
  });
}
